import React, { useState } from 'react';
import { Dropdown } from 'antd';
import { PhoneOutlined, DownOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import BaseButton from 'components/atoms/baseButton';
import { useCallButtons } from 'infrastructure/hooks/control-panel';
import { useAccess } from 'infrastructure/hooks/access/useAccess';
import { useGetDoNotCallInfo } from 'infrastructure/hooks/control-panel/use-get-call-info';
import ManualEnterCall from 'components/organisms/controlPanel/manualEnteredCallModal';
import useUser from 'utils/useUser';

import styles from './styles.module.scss';

import type { MenuProps } from 'antd';
import type { ModuleType } from 'infrastructure/enums';

interface Props {
  patientGuid: string;
  module: ModuleType;
}

const CallDropdownButton: React.FC<Props> = ({ patientGuid, module }) => {
  const { t } = useTranslation();
  const { userType, roleNames, timezone } = useUser();
  const [isRpmManualOpen, setIsRpmManualOpen] = useState(false);
  const [isCcmManualOpen, setIsCcmManualOpen] = useState(false);

  const {
    patientInfo,
    doctorInfo,
    isRpmModule,
    isCombinedModule,
    isBlockRpmModule,
    isBlockCcmModule,
    isCcmActionDisabled,
  } = useAccess({ module, patientGuid });

  const {
    doNotCallRpm,
    rpmDoNotCallReasonMessage,
    doNotCallCcm,
    ccmDoNotCallReasonMessage,
  } = useGetDoNotCallInfo();

  const { callButtons: rpmCallButtons, callButtonsAlert: rpmCallButtonsAlert } =
    useCallButtons({
      patientGuid,
      userType,
      doNotCall: doNotCallRpm,
      doNotCallReason: rpmDoNotCallReasonMessage,
      emergencyContactNumber: patientInfo?.emergencyContactNumber ?? '',
      patientHomeNumber: patientInfo?.homeNumber ?? '',
      patientPhone: patientInfo?.loginInfo?.phone ?? '',
      doctorPhone: doctorInfo?.phone,
      setRpmManualEnteredCallPopupOpen: setIsRpmManualOpen,
      roleNames,
      patientTimeZone: timezone,
      module,
    });

  const { callButtons: ccmCallButtons, callButtonsAlert: ccmCallButtonsAlert } =
    useCallButtons({
      patientGuid,
      userType,
      doNotCall: doNotCallCcm,
      doNotCallReason: ccmDoNotCallReasonMessage,
      emergencyContactNumber: patientInfo?.emergencyContactNumber ?? '',
      patientHomeNumber: patientInfo?.homeNumber ?? '',
      patientPhone: patientInfo?.loginInfo?.phone ?? '',
      doctorPhone: doctorInfo?.phone,
      setCcmManualEnteredCallPopupOpen: setIsCcmManualOpen,
      roleNames,
      patientTimeZone: timezone,
      module,
    });

  const buildMenuItems = (
    buttons: typeof rpmCallButtons,
    isRpm: boolean,
    keyPrefix: string,
  ): MenuProps['items'] =>
    buttons.map((btn, i) => ({
      key: `${keyPrefix}-${i}`,
      label: btn.label,
      disabled: btn.disabled,
      onClick: () => btn.handleClick?.(isRpm),
    }));

  const buildGroupedMenuItems = (
    buttons: typeof rpmCallButtons,
    isRpm: boolean,
    keyPrefix: string,
    groupLabel: string,
  ): MenuProps['items'] => [
    {
      type: 'group',
      key: `${keyPrefix}-group`,
      label: groupLabel,
      children: buildMenuItems(buttons, isRpm, keyPrefix),
    },
  ];

  const rpmUnavailable = isBlockRpmModule;
  const ccmUnavailable = isBlockCcmModule || isCcmActionDisabled;

  const rpmLabel = `${t('labels.rpm')} ${t('labels.call')}`;
  const ccmLabel = `${t('labels.ccm')} ${t('labels.call')}`;

  let menuItems: MenuProps['items'];

  if (isCombinedModule && !rpmUnavailable && !ccmUnavailable) {
    menuItems = [
      {
        key: 'rpm-call',
        label: rpmLabel,
        popupClassName: 'call-dropdown-submenu',
        popupOffset: [4, -4],
        children: buildGroupedMenuItems(rpmCallButtons, true, 'rpm', rpmLabel),
      },
      {
        key: 'ccm-call',
        label: ccmLabel,
        popupClassName: 'call-dropdown-submenu',
        popupOffset: [4, -4],
        children: buildGroupedMenuItems(ccmCallButtons, false, 'ccm', ccmLabel),
      },
    ];
  } else if (isCombinedModule && rpmUnavailable && !ccmUnavailable) {
    menuItems = buildGroupedMenuItems(ccmCallButtons, false, 'ccm', ccmLabel);
  } else if (isCombinedModule && !rpmUnavailable && ccmUnavailable) {
    menuItems = buildGroupedMenuItems(rpmCallButtons, true, 'rpm', rpmLabel);
  } else if (isCombinedModule) {
    menuItems = [
      {
        key: 'rpm-call',
        label: rpmLabel,
        disabled: true,
        popupClassName: 'call-dropdown-submenu',
        popupOffset: [4, -8],
        children: buildGroupedMenuItems(rpmCallButtons, true, 'rpm', rpmLabel),
      },
      {
        key: 'ccm-call',
        label: ccmLabel,
        disabled: true,
        popupClassName: 'call-dropdown-submenu',
        popupOffset: [4, -8],
        children: buildGroupedMenuItems(ccmCallButtons, false, 'ccm', ccmLabel),
      },
    ];
  } else if (isRpmModule) {
    menuItems = buildGroupedMenuItems(rpmCallButtons, true, 'rpm', rpmLabel);
  } else {
    menuItems = buildGroupedMenuItems(ccmCallButtons, false, 'ccm', ccmLabel);
  }

  return (
    <>
      <Dropdown
        menu={{ items: menuItems }}
        trigger={['click']}
        rootClassName={styles['call-dropdown-menu']}
      >
        <BaseButton
          label={t('labels.call')}
          icon={<PhoneOutlined />}
          rightIcon={
            <div className={styles['arrow-icon']}>
              <DownOutlined />
            </div>
          }
          type="secondary"
          className={styles['action-button']}
        />
      </Dropdown>
      <ManualEnterCall
        isOpen={isRpmManualOpen}
        toggle={setIsRpmManualOpen}
        patientGuid={patientGuid}
        isRpmPanel
      />
      <ManualEnterCall
        isOpen={isCcmManualOpen}
        toggle={setIsCcmManualOpen}
        patientGuid={patientGuid}
        isRpmPanel={false}
      />
      {rpmCallButtonsAlert}
      {ccmCallButtonsAlert}
    </>
  );
};

export default CallDropdownButton;
